import React, { useMemo } from 'react';
import { PolarAngleAxis, PolarGrid, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { SkillStats } from '../../models/userStats';

// Fix for recharts TypeScript compatibility with React 18
const TypedPolarAngleAxis = PolarAngleAxis as any;

interface SpiderChartProps {
    userStats: {
        username: string;
        stats: SkillStats;
        color: string;
    }[];
    showLegend?: boolean;
    height?: number;
}

const SpiderChart: React.FC<SpiderChartProps> = ({
    userStats,
    showLegend = true,
    height = 350
}) => {
    // Transform the data for recharts
    const chartData = useMemo(() => {
        const categories = [
            { key: 'rail', label: 'Rail/Jib' },
            { key: 'jump', label: 'Jump' },
            { key: 'style', label: 'Style' },
            { key: 'off_snow', label: 'Off-Snow' },
            { key: 'mountain', label: 'Freeride' },
            { key: 'halfpipe', label: 'Halfpipe' }
        ];

        return categories.map(category => {
            const dataPoint: any = {
                category: category.label,
                fullMark: 100
            };

            userStats.forEach((user, index) => {
                dataPoint[`user${index}`] = user.stats[category.key as keyof SkillStats];
            });

            return dataPoint;
        });
    }, [userStats]);

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg p-3 shadow-lg">
                    <p className="text-white font-semibold text-sm mb-2">{payload[0].payload.category}</p>
                    {payload.map((entry: any, index: number) => {
                        if (entry.dataKey !== 'fullMark') {
                            const userIndex = parseInt(entry.dataKey.replace('user', ''));
                            const user = userStats[userIndex];
                            return (
                                <div key={index} className="flex items-center gap-2 mt-1">
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: user.color }}
                                    />
                                    <span className="text-gray-300 text-xs">
                                        {user.username}: <span className="font-semibold text-white">{entry.value}</span>
                                    </span>
                                </div>
                            );
                        }
                        return null;
                    })}
                </div>
            );
        }
        return null;
    };

    // Custom legend
    const CustomLegend = () => {
        return (
            <div className="flex flex-wrap justify-center gap-4 mt-2 px-4">
                {userStats.map((user, index) => (
                    <div key={index} className="flex items-center gap-2">
                        <div
                            className="w-4 h-4 rounded-full shadow-lg"
                            style={{ backgroundColor: user.color }}
                        />
                        <span className="text-sm font-medium text-white">
                            {user.username}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="w-full [&_*]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-wrapper]:outline-none" style={{ outline: 'none' }}>
            <ResponsiveContainer width="100%" height={height}>
                <RadarChart data={chartData} style={{ outline: 'none' }}>
                    <defs>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    <PolarGrid
                        stroke="#374151"
                        strokeWidth={1}
                        strokeOpacity={0.5}
                    />

                    <TypedPolarAngleAxis
                        dataKey="category"
                        tick={{
                            fill: '#E5E7EB',
                            fontSize: 12,
                            fontWeight: 600
                        }}
                        tickLine={false}
                    />

                    {userStats.map((user, index) => (
                        <Radar
                            key={index}
                            name={user.username}
                            dataKey={`user${index}`}
                            stroke={user.color}
                            fill={user.color}
                            fillOpacity={0.25}
                            strokeWidth={2.5}
                            dot={{
                                r: 4,
                                strokeWidth: 2,
                                fill: user.color,
                                stroke: '#1F2937',
                            }}
                            activeDot={{
                                r: 6,
                                strokeWidth: 2,
                                fill: user.color,
                                stroke: '#1F2937',
                            }}
                        />
                    ))}

                    <Tooltip content={<CustomTooltip />} />
                </RadarChart>
            </ResponsiveContainer>

            {showLegend && <CustomLegend />}
        </div>
    );
};

export default SpiderChart;
